import { ChangeEvent } from "react";
import I18n from "common/i18n";
import { btnDelay, usePlaySound } from "common/utils";
import { PublicFilter, PieceSortOrder } from "features/builder/pieceList";

const FILTER_ITEMS: { kind: PublicFilter; labelKey: string; icon?: string }[] = [
  { kind: PublicFilter.OFFICIAL, labelKey: "MSG_BUILDER_PUBLIC_FILTER_OFFICIAL" },
  { kind: PublicFilter.COPIED, labelKey: "MSG_BUILDER_PUBLIC_FILTER_COPIED" },
  { kind: PublicFilter.TAG, labelKey: "MSG_BUILDER_PUBLIC_FILTER_TAG", icon: "label" },
];

export const PublicMapsHeader = ({
  count,
  keyword,
  activeFilters,
  sortOrder,
  onChangeKeyword,
  onToggleFilter,
  onToggleSort,
}: {
  count: number;
  keyword: string;
  activeFilters: PublicFilter[];
  sortOrder: PieceSortOrder;
  onChangeKeyword: (value: string) => void;
  onToggleFilter: (kind: PublicFilter) => void;
  onToggleSort: () => void;
}) => {
  const play = usePlaySound();

  const handleKeyword = (e: ChangeEvent<HTMLInputElement>) => {
    onChangeKeyword(e.target.value);
  };

  const handleToggleFilter = (kind: PublicFilter) => () => {
    play();
    btnDelay(() => onToggleFilter(kind));
  };

  const handleToggleSort = () => {
    play();
    btnDelay(() => onToggleSort());
  };

  return (
    <div className="flex-row-el items-center justify-between w-full px-2 py-2 shrink-0 gap-2">
      <div className="flex-row-el items-center gap-1 bg-blue-100 rounded-full px-3 py-1 shadow-sm shrink-0">
        <span className="material-symbols-outlined text-white type-sm">public</span>
        <p className="type-text type-sm text-white whitespace-nowrap">
          {I18n.t("MSG_BUILDER_PUBLIC_COUNT", { count })}
        </p>
      </div>

      <div className="flex-row-el items-center gap-1 bg-white rounded-full px-3 py-1 border-solid border-2 border-blue-100 w-44 shrink-0">
        <span className="material-symbols-outlined text-blue-100 type-sm">search</span>
        <input
          type="text"
          value={keyword}
          onChange={handleKeyword}
          placeholder={I18n.t("MSG_BUILDER_PUBLIC_SEARCH_PLACEHOLDER")}
          className="type-text type-sm text-textcolor-black bg-transparent border-0 outline-none w-full"
        />
      </div>

      <div className="flex-row-el items-center gap-2 min-w-0 overflow-x-auto invisible-scrollbar">
        {FILTER_ITEMS.map(({ kind, labelKey, icon }) => {
          const active = activeFilters.includes(kind);
          return (
            <button
              key={kind}
              type="button"
              onClick={handleToggleFilter(kind)}
              className={`type-inherit-font flex-row-center shrink-0 gap-1 px-3 py-1 rounded-full border-solid border-2 cursor-pointer ${
                active ? "bg-blue-100 border-blue-100" : "bg-white border-blue-100"
              }`}
            >
              {icon && (
                <span
                  className={`material-symbols-outlined type-sm ${
                    active ? "text-white" : "text-textcolor-black"
                  }`}
                >
                  {icon}
                </span>
              )}
              <p
                className={`type-text type-sm whitespace-nowrap ${
                  active ? "text-white" : "text-textcolor-black"
                }`}
              >
                {I18n.t(labelKey)}
              </p>
            </button>
          );
        })}
      </div>

      <div className="flex-row-el items-center upsilon shrink-0">
        <button type="button" onClick={handleToggleSort} className="type-inherit-font btn btn-blue">
          <div className="flex-row-center gap-1">
            <span className="material-symbols-outlined text-white type-sm">swap_vert</span>
            <p className="type-text type-sm text-white whitespace-nowrap">
              {sortOrder === PieceSortOrder.DESC
                ? I18n.t("MSG_BUILDER_SORT_POPULAR")
                : I18n.t("MSG_BUILDER_SORT_ASC")}
            </p>
          </div>
        </button>
      </div>
    </div>
  );
};
